import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://agrosmart.md';
const SITE_NAME = 'AgroSmart.md';
const DEFAULT_DESC =
  'AgroSmart.md oferă utilaje agricole, motocultoare, mori, sere și echipamente pentru gospodărie în Moldova. Produse practice pentru agricultură, grădină și ferme.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/favicon.png`;

/**
 * SEO component:
 *   - Sets title / description / canonical
 *   - Outputs OpenGraph + Twitter meta
 *   - Adds hreflang for ro/ru/x-default
 *   - Accepts JSON-LD object (or array) via `jsonLd`
 *   - Accepts breadcrumbs as [{name, url}]
 *
 * Usage:
 *   <SEO
 *     title="Sapă ergonomică Fiskars"
 *     description="..."
 *     image="https://..."
 *     type="product"
 *     jsonLd={{ ...productSchema }}
 *     breadcrumbs={[{name:'Acasă',url:'/'},{name:'Unelte',url:'/category/unelte'}]}
 *   />
 */
const SEO = ({
  title,
  description,
  image,
  type = 'website',
  noindex = false,
  jsonLd = null,
  breadcrumbs = null,
}) => {
  const location = useLocation();

  // Strip locale prefixes ("/ru") so canonical points at the canonical RO URL.
  const path = location.pathname || '/';
  const cleanPath = path.replace(/^\/ru(?=\/|$)/, '') || '/';
  const canonical = `${SITE_URL}${cleanPath}`;
  const ruUrl = `${SITE_URL}/ru${cleanPath === '/' ? '' : cleanPath}`;

  const pageTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} – Utilaje Agricole, Motocultoare, Mori și Sere în Moldova`;
  const desc = description || DEFAULT_DESC;
  const ogImage = image || DEFAULT_OG_IMAGE;

  // Auto-append breadcrumb JSON-LD when provided
  const ldNodes = [];
  if (jsonLd) {
    if (Array.isArray(jsonLd)) ldNodes.push(...jsonLd);
    else ldNodes.push(jsonLd);
  }
  if (breadcrumbs && breadcrumbs.length) {
    ldNodes.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: b.url?.startsWith('http') ? b.url : `${SITE_URL}${b.url || ''}`,
      })),
    });
  }

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow,max-image-preview:large" />}

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="ro" href={canonical} />
      <link rel="alternate" hrefLang="ru" href={ruUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title || SITE_NAME} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="ro_RO" />
      <meta property="og:locale:alternate" content="ru_RU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || SITE_NAME} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {ldNodes.map((node, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
export { SITE_URL, SITE_NAME };
